/**
 * US-PB-SETCARD — Öffentliche Setcard-Seite (ohne Account).
 *
 * Aufruf über `/setcard/{token}`. Der Nutzer gibt den 4-stelligen PIN ein.
 * Bei korrektem PIN wird die Setcard als HTML-Ansicht gerendert (SetcardView).
 * Ein "Als PDF anzeigen"-Button öffnet das PDF in einem neuen Tab.
 *
 * Wird in `main.tsx` VOR dem Auth-Gate gerendert.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { BRAND, getThemeColors } from '../../lib/theme';
import { Button, Card } from '../../components/ui';
import { openPublicSetcard, openPublicSetcardAsAdmin, openPublicSetcardPdf, openPublicSetcardPdfAsAdmin, type SetcardData } from '../../lib/api';
import { SetcardView } from '../pflege/profile/SetcardView';

interface Props {
  token: string;
}

/** Fehlercode → Kundentext (übersetzt). */
function apiErrorMessage(err: unknown, tr: (key: string, opts?: Record<string, unknown>) => string): string {
  const e = err as { response?: { status?: number; data?: { errors?: Array<{ code?: string }>; meta?: { remaining_attempts?: number } } } };
  const status = e?.response?.status;
  const code = e?.response?.data?.errors?.[0]?.code;
  const remaining = e?.response?.data?.meta?.remaining_attempts;

  if (code === 'E_INVALID_PIN' || status === 422) {
    return remaining !== undefined && remaining > 0
      ? tr('public.setcard.error.pin_wrong_remaining', { count: remaining })
      : tr('public.setcard.error.pin_wrong');
  }
  if (code === 'E_PIN_THROTTLED' || status === 429) {
    return tr('public.setcard.error.pin_throttled');
  }
  if (code === 'E_LINK_404' || status === 404) {
    return tr('public.schedule.error.link_invalid');
  }
  return tr('public.schedule.error.generic');
}

export function PublicSetcardPage({ token }: Props) {
  const { t: tr } = useTranslation();
  const { dark } = useTheme();
  const tc = getThemeColors(dark);

  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setcardData, setSetcardData] = useState<SetcardData | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  // War der /open-Aufruf ein Admin-Bypass (kein PIN)? Entscheidet, welche
  // PDF-Variante handlePdf aufruft — siehe Kommentar dort (Bug B1).
  const [isAdminBypass, setIsAdminBypass] = useState(false);

  // ── Admin-Bypass: eingeloggte Admins überspringen das PIN-Gate ─────────────
  // Prüft beim Mount ob ein Admin-Token in localStorage liegt. Falls ja, wird
  // der open-Endpunkt direkt mit dem Bearer-Token aufgerufen — kein PIN nötig.
  useEffect(() => {
    const adminToken = (() => { try { return localStorage.getItem('sh24.token'); } catch { return null; } })();
    if (!adminToken) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await openPublicSetcardAsAdmin(token);
        if (!cancelled) {
          setSetcardData(data);
          setIsAdminBypass(true);
        }
      } catch {
        // Not a valid admin session — stay on PIN stage.
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleOpen = async () => {
    if (!/^\d{4}$/.test(pin)) {
      setError(tr('public.schedule.pin_required'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await openPublicSetcard(token, pin);
      setSetcardData(data);
    } catch (err) {
      setError(apiErrorMessage(err, tr));
    } finally {
      setBusy(false);
    }
  };

  const handlePdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      // Bug B1 (Sammelpaket 22.09.2026): im Admin-Bypass (eingeloggter Admin,
      // kein PIN eingegeben) lief dieser Button immer ins Leere, weil er
      // unabhängig vom Bypass den öffentlichen PIN-Endpunkt mit leerem `pin`
      // aufrief (apiPublic, kein Bearer) → E_INVALID_PIN. Jetzt: im
      // Admin-Bypass die authentifizierte Variante (apiV2, Bearer-Token)
      // nutzen, sonst wie bisher PIN-basiert.
      const blob = isAdminBypass
        ? await openPublicSetcardPdfAsAdmin(token)
        : await openPublicSetcardPdf(token, pin);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      // silently ignore — PDF download is secondary
    } finally {
      setPdfBusy(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    fontSize: 14, padding: '10px 12px', border: `1px solid ${tc.border}`, borderRadius: 8,
    background: tc.inputBg, color: tc.text, fontFamily: 'Outfit,sans-serif', outline: 'none',
    boxSizing: 'border-box', width: '100%',
  };

  const page = (children: React.ReactNode) => (
    // height + overflowY statt minHeight: html/body/#root stehen global auf
    // overflow:hidden (App-Shell) — ohne eigenen Scroll-Container wäre die
    // Setcard-Seite nicht scrollbar.
    <div style={{ height: '100vh', overflowY: 'auto', background: tc.bg, padding: '24px 16px', fontFamily: 'Outfit,sans-serif' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: BRAND }}>Senihelp24</span>
          <span style={{ fontSize: 14, color: tc.muted }}>{tr('public.setcard.header_subtitle')}</span>
        </div>
        {children}
      </div>
    </div>
  );

  // Stage 1: PIN gate
  if (!setcardData) {
    return page(
      <Card dark={dark} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: tc.heading }}>{tr('public.setcard.pin_stage.title')}</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: tc.muted }}>
            {tr('public.schedule.pin_stage.subtitle')}
          </p>
        </div>
        <form
          onSubmit={e => { e.preventDefault(); handleOpen(); }}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <input
            aria-label={tr('public.schedule.pin_stage.pin_aria')}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={4}
            value={pin}
            onChange={e => { setPin(e.target.value.replace(/\D/g, '').slice(0, 4)); setError(null); }}
            placeholder="••••"
            style={{ ...inputStyle, fontSize: 28, letterSpacing: '0.5em', textAlign: 'center', maxWidth: 220 }}
          />
          {error && (
            <div role="alert" style={{ padding: '10px 12px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fda29b', borderRadius: 8, fontSize: 13.5, color: '#b42318' }}>
              {error}
            </div>
          )}
          <div>
            <Button dark={dark} variant="primary" disabled={busy} onClick={handleOpen}>
              {busy ? tr('public.schedule.please_wait') : tr('common.action.continue')}
            </Button>
          </div>
        </form>
      </Card>,
    );
  }

  // Stage 2: HTML Setcard view + PDF button
  return page(
    <>
      {/* PDF action bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={handlePdf}
          disabled={pdfBusy}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'transparent', color: BRAND,
            border: `1px solid ${BRAND}`, borderRadius: 8,
            padding: '7px 14px', fontSize: 13, fontWeight: 600,
            fontFamily: 'Outfit,sans-serif',
            cursor: pdfBusy ? 'wait' : 'pointer', opacity: pdfBusy ? 0.7 : 1,
          }}
        >
          <span style={{ fontSize: 13 }}>⬇</span>
          {pdfBusy ? tr('public.schedule.please_wait') : tr('public.setcard.view_pdf')}
        </button>
      </div>
      <Card dark={dark} style={{ padding: 24 }}>
        <SetcardView data={setcardData} dark={dark} />
      </Card>
    </>,
  );
}
